import accessibility from "./accessibility";
import { mobileHeaderFavouritesCounter } from "./hoasFavouritesDom";

/**
 * Hoas favourites Helper methods to manage localstorage related functions
 *
 */
export const AREA_KEY = "areas";
export const COSTUNIT_KEY = "cost-units";
export const APARTMENT_KEY = "apartments";

/**
 * Create cost-unit object
 * @param {object} card created from costUnit data
 * @returns {object}
 */
export const createCostUnitObject = (card) => {
  return {
    id: card?.objectID?.substring(card?.objectID.indexOf("::") + 2),
    address: card?.address,
    location: card?.quarter_of_town + ", " + card?.city,
    rents: card?.rents,
    surface_areas: card?.surface_areas,
    type: COSTUNIT_KEY,
    city: card?.city,
    area: card?.quarter_of_town,
    analyticsType: "Kohde",
  };
};

/**
 * Create apartment object
 * @param {object} card
 * @returns {object}
 */
export const createApartmentObject = (card) => {
  //Each apartmnent add to favourite click
  let type = null;
  if (card?.type === "Huone") {
    type = "1H";
  } else if (card?.type === "Huoneisto") {
    type = card?.apartment_type;
  }

  return {
    id: card?.id,
    cost_unit_id: card?.cost_unit_id,
    address: card?.name,
    location: card?.quarter_of_town + ", " + card?.city,
    rent: card?.rent, //rents
    surface_area: card?.surface_area, //surface_areas
    apartment_type: type,
    apartment_category: card?.apartment_category,
    type: APARTMENT_KEY,
    city: card?.city,
    area: card.quarter_of_town,
    costUnitAddress: card?.address,
    analyticsType: "Asunto",
  };
};

/**
 * Return localstorage data by key
 * @param {string} key localstorage key
 * @returns {Array|null}
 */
export const getLocalStorage = (key) => {
  //To get result by key from localstorage
  const string = localStorage.getItem(key);

  if (typeof string !== "string") {
    return null;
  }

  return getParsedObject(string) ?? null;
};

/**
 * Remove element from localstorage
 * @param {string} storageKey Localstorage key
 * @param {string} targetValue Target value to check against
 * @param {string} objectKey key of the object where check targetValue
 */
export const removeFromLocalStorage = (storageKey, targetValue, objectKey) => {
  if (!storageKey || !targetValue || !objectKey) {
    return;
  }
  let items = getLocalStorage(storageKey);

  if (!items.length) {
    console.warn("Trying to remove from localstorage when its empty");
    return;
  }

  let final = items?.filter(function (obj) {
    return (
      obj[objectKey] !== targetValue &&
      obj[objectKey].toString() !== targetValue.toString()
    );
  });
  setLocalStorage(storageKey, final);
};

/**
 * Set localstorage items
 * @param {string} key localStorage key
 * @param {array} elements Array of new elements
 */
export const setLocalStorage = (key, elements) => {
  localStorage.setItem(key, JSON.stringify(elements));
  mobileHeaderFavouritesCounter();
};

/**
 * Find value from localstorage
 * @param {string} localStorageKey LocalstorageKey
 * @param {string|number} targetValue value to check against
 * @param {*} objectKey key of the object to check against
 * @returns {object}
 */
export const foundInLocalStorage = (
  localStorageKey,
  targetValue,
  objectKey,
) => {
  let result = null;
  let items = getLocalStorage(localStorageKey);

  if (!items) {
    return result;
  }

  if (objectKey && targetValue && Array.isArray(items)) {
    const itemFound = items.find(function (localStorageItem) {
      return (
        localStorageItem[objectKey] === targetValue ||
        localStorageItem[objectKey]?.toString() === targetValue?.toString()
      );
    });

    if (itemFound) {
      result = itemFound;
    }
  }

  return result;
};

/**
 * Add data to localstorage by localstorage key
 * @param {any} value Value to be added to localstorage
 * @param {string} storageKey localStorage key
 * @returns {void}
 */
export const addToLocalStorage = (value, storageKey) => {
  if (!value || !storageKey) {
    return;
  }

  const currentItems = getLocalStorage(storageKey);
  let merged = [value];

  if (Array.isArray(currentItems)) {
    merged = [...currentItems, value];
  }
  setLocalStorage(storageKey, merged);
};

/**
 * Construct translated name for apartment
 * @param {string} name
 * @returns {string}
 */
export const constructApartmentName = (name) => {
  if (window?.searchApp?.i18n) {
    const arrayOfKeys = Object.keys(window["searchApp"]["i18n"]);

    arrayOfKeys.forEach((key) => {
      if (name.includes(key)) {
        const translatable = translatei18nDB(key);
        name = name.replace(key, "");
        name = name + " " + translatable;
      }
    });
  }
  return name;
};

/**
 * Get translation from searchApp
 * @param {string} string to be translated
 * @returns {string}
 */
export const translatei18nDB = (string = null) => {
  //This uses string translations with polylang from database --> Kaksio/2 rooms --> Kaksio
  if (window["searchApp"]["i18n"][string] !== undefined) {
    return window["searchApp"]["i18n"][string];
  }

  return string;
};

/**
 * Check if given url has parameter given param
 * @param {string} url
 * @param {string} param_name
 * @param {string|number} value
 * @returns {boolean}
 */
export const hasParameter = (url, param_name, value) => {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);

  if (!params.has(param_name)) {
    return false;
  }

  return params.get(param_name) === value;
};

/**
 * Check if current page is edit form page
 * @returns {boolean}
 */
export const formIsEditForm = () => {
  const url = window.location.href;
  const paramToCheck = "view";
  const valueToCheck = "editApplication";
  return hasParameter(url, paramToCheck, valueToCheck);
};

/**
 * Check if current page is edit attachments page
 * @returns {boolean}
 */
export const isEditAttachments = () => {
  const url = window.location.href;
  const paramToCheck = "view";
  const valueToCheck = "updateAttachments";
  return hasParameter(url, paramToCheck, valueToCheck);
};

/**
 * Check if has element in localstorage from stringified object
 * @param {object} favouriteObject
 * @return {object | null}
 */
export const getElementFromLocalStorage = (favouriteObject) => {
  if (!favouriteObject) {
    console.error(
      "Missing favouriteObject in: getElementFromLocalStorage",
      favouriteObject,
    );
    return null;
  }
  const type = favouriteObject?.type;
  const key = "id";
  const targetValue = favouriteObject[key];
  if (!type || !isValidKey(type)) {
    console.error("Missing type: findElement", type);
    return null;
  }
  return foundInLocalStorage(type, targetValue, key);
};

/**
 * Check if given key is valid
 * @param {string} key Localstorage key
 * @returns {boolean}
 */
export const isValidKey = (key) => {
  const array = [AREA_KEY, COSTUNIT_KEY, APARTMENT_KEY];
  if (array.includes(key)) {
    return true;
  }
  return false;
};

/**
 * Return key from localstorage obejct that is needed to announce f.e message
 * @param {string} objectType
 * @returns {string | null}
 */
export const getKey = (objectType) => {
  if (!isValidKey(objectType)) {
    console.error("Invalid key from DOM attribute in: getKey", objectType);
    return null;
  }

  switch (objectType) {
    case COSTUNIT_KEY:
      return "address";
    case APARTMENT_KEY:
      return "address";
    case AREA_KEY:
      return "name"; // Return WP name
    default:
      return null;
  }
};

/**
 * Json parse stringified object
 * @param {*} stringifiedObject
 * @returns {any}
 */
export const getParsedObject = (stringifiedObject) => {
  if (typeof stringifiedObject !== "string") {
    console.error(
      "stringifiedObject must be string: getParsedObject",
      stringifiedObject,
    );
    return null;
  }
  return JSON.parse(stringifiedObject);
};

/**
 * Announce message by getting value from data by key and join it with message
 * @param {any} data
 * @param {string} key
 * @param {string} message
 * @returns {void}
 */
export const announceMessageFromData = (data, key, message) => {
  if (!data || !key || !message) {
    console.error(
      "Missing data, key or message: announceMessageFromData",
      data,
      key,
      message,
    );
    return;
  }
  const value = data[key];

  if (!value) {
    console.error("Missing value: announceMessageFromData", value);
    return;
  }
  const { globalAccessibilitySpeakerId, announceMessage } = accessibility();

  const ariaLiveMessage = value + " " + message;
  announceMessage(globalAccessibilitySpeakerId, ariaLiveMessage);
};
